<template>
  <div id="appointment-communication" class="mb-5 py-2">
    <h4 class="mb-0">{{ $t('homelab.labels.communication') }}</h4>
    <div class="d-flex align-items-center mt-2">
      <va-button size="small" class="communication-open-btn" @click="isModalOpen = true">
        <div class="d-flex flex-column align-items-center">
          <span>
            <i class="fa fa-comments mr-1"></i>
            {{ messages.length > 0 ? $t('homelab.labels.communication_open') : $t('homelab.labels.communication_start') }}
          </span>
          <span v-if="!loading && messages.length > 0" class="communication-count mt-1">
            {{ $t('homelab.labels.communication_count', { count: messages.length }) }}
          </span>
        </div>
      </va-button>
    </div>

    <va-modal
      id="appointment-communication-modal"
      :value="isModalOpen"
      :hideDefaultActions="true"
      @cancel="isModalOpen = false"
      @input="val => { isModalOpen = val }"
    >
      <div slot="header" class="modal-title">
        {{ $t('homelab.labels.communication') }} – {{ appointment.name }}
      </div>

      <div class="communication-modal-content">
        <div v-if="loading" class="text-muted py-4 text-center">
          {{ $t('homelab.labels.communication_loading') }}
        </div>

        <div v-else>
          <div v-if="messages.length === 0" class="text-muted py-4 text-center">
            {{ $t('homelab.labels.communication_no_messages') }}
          </div>

          <div v-else class="communication-list mb-4">
            <div
              v-for="msg in messages"
              :key="msg.id"
              :class="['communication-item', 'p-3', 'mb-2', 'rounded', msg.sender_type === 'professional' ? 'communication-from-lab' : 'communication-from-patient']"
            >
              <div class="d-flex justify-content-between align-items-start mb-1">
                <span
                  class="badge px-2 communication-sender-badge"
                  :class="msg.sender_type === 'professional' ? 'communication-sender-badge--lab' : 'communication-sender-badge--patient'"
                >
                  {{ msg.sender_type === 'professional' ? $t('homelab.labels.sender_professional_with_facility', { facility: facilityDisplayForProfessional }) : $t('homelab.labels.sender_patient_with_name', { name: patientName }) }}
                </span>
                <span class="text-muted small">{{ msg.created_at | localTime('YYYY-MM-DD HH:mm') }}</span>
              </div>
              <p class="mb-0 communication-content">{{ msg.content }}</p>
            </div>
          </div>

          <div v-if="hasWriteAppointmentsPermissions && !isAppointmentCanceled" class="communication-form">
            <va-input
              v-model="newMessage"
              type="textarea"
              :label="$t('homelab.labels.communication_message_label')"
              :placeholder="$t('homelab.labels.communication_message_placeholder')"
              :rows="3"
              class="mb-2"
            />
            <va-button
              :disabled="!newMessage.trim() || sending"
              @click="sendMessage"
            >
              {{ sending ? $t('homelab.labels.communication_sending') : $t('homelab.labels.communication_send') }}
            </va-button>
          </div>
        </div>
      </div>

      <div slot="actions" class="d-flex justify-content-end mt-3">
        <va-button size="small" @click="isModalOpen = false">
          {{ $t('modals.buttons.back') }}
        </va-button>
      </div>
    </va-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { AppointmentCommunicationService } from '@/api/HomelabService'

export default {
  name: 'AppointmentCommunication',
  props: {
    appointment: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isModalOpen: false,
      messages: [],
      loading: false,
      newMessage: '',
      sending: false
    }
  },
  computed: {
    ...mapState('homelab', ['infoTypes', 'accessTypes']),
    homelabService () {
      return this.$store.state.services.find(service => service.url === this.$store.state.homelab_server)
    },
    entityName: function entityName() {
      var entity = (this.appointment && this.appointment.entity_of_origin) ||
        (this.appointment && this.appointment.entity);
      return (entity && entity.name) || 'N/A';
    },
    /** Igual à coluna Instalação na tabela (nome + cidade), com fallback à entidade se não houver facility */
    facilityDisplayForProfessional () {
      const facility = this.appointment && this.appointment.facility
      if (facility) {
        if (facility.address && facility.address.city) {
          return `${facility.name} (${facility.address.city})`
        }
        if (facility.name) {
          return facility.name
        }
      }
      return this.entityName
    },
    patientName: function() {
      return this.appointment && this.appointment.name ? this.appointment.name : 'N/A'
    },
    hasWriteAppointmentsPermissions () {
      if (!this.appointment || !this.homelabService) return false
      let hasSomePermission = false
      if(this.appointment.entity_of_origin) {
        if(this.homelabService.user_permissions.some(
          permission =>
            (permission.entity_id === null || permission.entity_id === this.appointment.entity_of_origin.id) &&
            permission.access_type > this.accessTypes.READ &&
            permission.info_type === this.infoTypes.APPOINTMENTS
        )) hasSomePermission = true
      }

      if(this.homelabService.user_permissions.some(
        permission =>
          (permission.entity_id === null || permission.entity_id === this.appointment.entity.id) &&
          permission.access_type > this.accessTypes.READ &&
          permission.info_type === this.infoTypes.APPOINTMENTS
      )) hasSomePermission = true

      return hasSomePermission
    },
    isAppointmentCanceled () {
      const statusId = this.appointment && this.appointment.status ? this.appointment.status.id : null
      return statusId === 20
    }
  },
  watch: {
    'appointment.id': {
      handler (id) {
        if (id) this.loadMessages()
      }
    }
  },
  created () {
    if (this.appointment && this.appointment.id) {
      this.loadMessages()
    }
  },
  methods: {
    loadMessages () {
      if (!this.appointment || !this.appointment.id) return
      this.loading = true
      AppointmentCommunicationService.getMessages(this.appointment.id)
        .then(messages => {
          this.messages = Array.isArray(messages) ? messages : []
        })
        .catch(errors => {
          console.error(errors)
          this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.loading = false
        })
    },
    sendMessage () {
      const content = (this.newMessage || '').trim()
      if (!content || this.sending || !this.appointment || !this.appointment.id || this.isAppointmentCanceled) return
      this.sending = true
      AppointmentCommunicationService.store(this.appointment.id, { content })
        .then(message => {
          this.messages = [...this.messages, message]
          this.newMessage = ''
          this.showToast(this.$t('homelab.communication.message_sent'), this.$store.state.successToastOptions)
          this.$emit('appointment-communication-updated', { messages: this.messages })
        })
        .catch(errors => {
          console.error(errors)
          this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.sending = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
/* Só o que fica dentro do #appointment-communication no DOM real */
#appointment-communication {
  .communication-open-btn .communication-count {
    font-size: 0.85em;
    opacity: 0.9;
  }
}
</style>

<style lang="scss">
/* VaModal faz append ao body; scoped pode não marcar o slot. Estilos globais limitados a .communication-modal-content */
.communication-modal-content {
  .communication-list {
    max-height: 400px;
    overflow-y: auto;
  }

  /* Azul com mais cor que #e8f4fd — equilíbrio visual com o bloco utente (#b8e0d9) */
  .communication-from-lab {
    background-color: #a8d4f0 !important;
    border-left: 3px solid #007bff !important;
  }

  /* Verde água (iOS ~ rgb 184,224,217) */
  .communication-from-patient {
    background-color: #b8e0d9 !important;
    border-left: 3px solid #5eb8aa !important;
  }

  .communication-sender-badge {
    font-weight: 600;
    border-radius: 0.25rem;
    display: inline-block;
    padding: 0.35em 0.5em;
  }

  .communication-sender-badge--lab {
    background-color: #007bff !important;
    color: #fff !important;
  }

  .communication-sender-badge--patient {
    background-color: #5eb8aa !important;
    color: #fff !important;
  }

  .communication-content {
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
